/**
 * Email Verification Page
 * Verify email address with token
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, Mail, Send } from 'lucide-react';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import vizzexLogo from '../../assets/VIZZEX.250.png';

type VerifyStatus = 'loading' | 'success' | 'error' | 'pending';

export default function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, refreshUser } = useAuth();
  
  const token = searchParams.get('token');
  
  const [status, setStatus] = useState<VerifyStatus>(token ? 'loading' : 'pending');
  const [message, setMessage] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  // Handle token verification
  useEffect(() => {
    if (token) {
      const verifyEmail = async () => {
        try {
          setStatus('loading');
          const response = await api.get(`/api/auth/verify-email?token=${token}`);

          if (response.data.success) {
            setStatus('success');
            setMessage(response.data.email || 'Your email has been verified!');
            
            // Refresh user data if logged in
            if (isAuthenticated) {
              await refreshUser();
            }

            // Redirect to dashboard after delay
            setTimeout(() => {
              navigate('/dashboard', { replace: true });
            }, 2500);
          } else {
            throw new Error(response.data.error || 'Verification failed');
          }
        } catch (err) {
          console.error('Email verification error:', err);
          const error = err as { response?: { data?: { error?: string } }; message?: string };
          setStatus('error');
          setMessage(error.response?.data?.error || error.message || 'Invalid or expired verification link');
        }
      };

      verifyEmail();
    }
  }, [token, navigate, isAuthenticated, refreshUser]);

  // Handle resend verification email
  const handleResend = async () => {
    if (!user?.email) return;
    
    setResendLoading(true);
    try {
      // This would call a resend verification endpoint
      // For now, just simulate success
      await new Promise(resolve => setTimeout(resolve, 1000));
      setResendSent(true);
    } catch (err) {
      console.error('Resend error:', err);
    } finally {
      setResendLoading(false);
    }
  };

  // Token verification flow
  if (token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="w-full max-w-md animate-slide-up">
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src={vizzexLogo} 
              alt="VizzEx Pro" 
              className="w-[250px] mx-auto mb-4"
            />
            <h1 className="text-2xl font-bold gradient-text">VizzEx Pro</h1>
          </div>

          {/* Status Card */}
          <div className="glass rounded-2xl p-8 text-center">
            {status === 'loading' && (
              <>
                <div className="w-16 h-16 rounded-full bg-[var(--color-primary-500)]/20 flex items-center justify-center mx-auto mb-4">
                  <Loader2 className="w-8 h-8 text-[var(--color-primary-400)] animate-spin" />
                </div>
                <h2 className="text-xl font-semibold mb-2">Verifying...</h2>
                <p className="text-[var(--text-secondary)]">Please wait while we verify your email.</p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 rounded-full bg-[var(--color-success)]/20 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-[var(--color-success)]" />
                </div>
                <h2 className="text-xl font-semibold mb-2">Email Verified!</h2>
                <p className="text-[var(--text-secondary)]">{message}</p>
                <p className="text-sm text-white/70 mt-4">Redirecting to dashboard...</p>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-16 h-16 rounded-full bg-[var(--color-error)]/20 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-8 h-8 text-[var(--color-error)]" />
                </div>
                <h2 className="text-xl font-semibold mb-2">Verification Failed</h2>
                <p className="text-[var(--text-secondary)] mb-6">{message}</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link
                    to="/login"
                    className="px-6 py-3 bg-[var(--bg-tertiary)] text-white font-medium rounded-lg hover:bg-[var(--bg-tertiary)]/80 transition-colors text-center"
                  >
                    Back to Login
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Pending verification (no token) - show resend option
  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md animate-slide-up">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[var(--color-primary-400)] to-[var(--color-accent-500)] flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-bold text-2xl">V</span>
          </div>
          <h1 className="text-2xl font-bold gradient-text">VizzEx Pro</h1>
          <p className="text-[var(--text-secondary)] mt-2">Verify your email</p>
        </div>

        <div className="glass rounded-2xl p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-[var(--color-warning)]/20 flex items-center justify-center mx-auto mb-4">
            <Mail className="w-8 h-8 text-[var(--color-warning)]" />
          </div>
          
          <h2 className="text-xl font-semibold mb-2">Check your inbox</h2>
          <p className="text-[var(--text-secondary)] mb-6">
            We've sent a verification email to{' '}
            <strong>{user?.email || 'your email address'}</strong>.
            Click the link in the email to verify your account.
          </p>

          {resendSent ? (
            <div className="p-4 bg-[var(--color-success)]/10 border border-[var(--color-success)]/30 rounded-lg mb-4">
              <p className="text-[var(--color-success)] text-sm">
                Verification email sent! Check your inbox.
              </p>
            </div>
          ) : (
            <button
              onClick={handleResend}
              disabled={resendLoading}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--bg-tertiary)] text-white font-medium rounded-lg hover:bg-[var(--bg-tertiary)]/80 transition-colors disabled:opacity-50"
            >
              {resendLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              Resend verification email
            </button>
          )}

          <div className="mt-6 pt-6 border-t border-[var(--bg-tertiary)]">
            <p className="text-sm text-white/70">
              Wrong email?{' '}
              <Link to="/register" className="text-[var(--color-primary-400)] hover:underline">
                Create a new account
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}


